import React from "react";
import { motion } from "framer-motion";
import { ArrowDown, Calendar } from "lucide-react";
import { useLanguage } from "../../contexts/LanguageContext";
import { openCalendly } from "../../lib/calendly";

const HERO_IMG =
  "https://static.prod-images.emergentagent.com/jobs/d69863e8-72c5-45eb-a555-03296866412e/images/55f602fa6bd1b77132ae0dcb223658138d6d9ffd083cf083367c20b6e2932dcb.png";

export const Hero = () => {
  const { t } = useLanguage();

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden bg-[#0D2F4C]"
    >
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src={HERO_IMG}
          alt="Financial district"
          className="w-full h-full object-cover opacity-70"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0D2F4C] via-[#0D2F4C]/85 to-[#0D2F4C]/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0D2F4C]/80 via-transparent to-transparent" />
      </div>

      {/* Subtle grain */}
      <div
        className="absolute inset-0 opacity-[0.04] mix-blend-overlay pointer-events-none"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100'%3E%3Cfilter id='n'%3E%3CfeTurbulence baseFrequency='0.9'/%3E%3C/filter%3E%3Crect width='100' height='100' filter='url(%23n)'/%3E%3C/svg%3E\")",
        }}
      />

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-8 min-h-screen flex flex-col justify-center pt-32 pb-24">
        {/* Animated crest — elegant fade-in + scale, sets the tone */}
        <motion.div
          initial={{ opacity: 0, scale: 0.7, y: -10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 1.4, delay: 0.05, ease: [0.22, 1, 0.36, 1] }}
          className="mb-8"
          data-testid="hero-crest"
        >
          <img
            src="/logo-gold-200.png"
            alt="Richild Invest crest"
            className="w-20 h-20 sm:w-24 sm:h-24 object-contain drop-shadow-[0_2px_18px_rgba(201,169,97,0.35)]"
            loading="eager"
          />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="flex items-center gap-4 mb-10"
        >
          <span className="h-px w-12 bg-white/60" />
          <span
            data-testid="hero-overline"
            className="text-xs uppercase tracking-[0.3em] text-white/80"
          >
            {t.hero.overline}
          </span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.55 }}
          data-testid="hero-title"
          className="font-serif text-white text-5xl sm:text-6xl lg:text-7xl xl:text-8xl leading-[1.05] tracking-tight max-w-5xl"
          style={{ fontFamily: "'Playfair Display', serif" }}
        >
          {t.hero.title_1}
          <br />
          <span className="italic font-light">{t.hero.title_2}</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.75 }}
          data-testid="hero-subtitle"
          className="text-white/75 text-base sm:text-lg max-w-xl mt-10 leading-relaxed"
        >
          {t.hero.subtitle}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.95 }}
          className="flex flex-wrap gap-4 mt-12"
        >
          <button
            data-testid="hero-cta-services-btn"
            onClick={() => scrollTo("services")}
            className="bg-white text-[#0D2F4C] px-8 py-4 text-sm tracking-wide hover:bg-white/90 transition-all duration-300 group flex items-center gap-3"
            style={{ borderRadius: 0 }}
          >
            <span>{t.hero.cta_primary}</span>
            <span className="inline-block w-6 h-px bg-[#0D2F4C] transition-all duration-300 group-hover:w-10" />
          </button>
          <button
            data-testid="hero-cta-book-btn"
            onClick={() => openCalendly()}
            className="group relative bg-[#C9A961] text-[#0D2F4C] px-8 py-4 text-sm tracking-wide hover:bg-[#D4B775] transition-all duration-300 flex items-center gap-3 shadow-[0_0_28px_-6px_rgba(201,169,97,0.55)]"
            style={{ borderRadius: 0 }}
          >
            <Calendar size={16} strokeWidth={2.2} />
            <span className="font-medium">{t.hero.cta_book}</span>
            <span className="inline-block w-6 h-px bg-[#0D2F4C] transition-all duration-300 group-hover:w-10" />
          </button>
          <button
            data-testid="hero-cta-offices-btn"
            onClick={() => scrollTo("presence")}
            className="border border-white/40 text-white px-8 py-4 text-sm tracking-wide hover:bg-white/10 transition-colors duration-300"
            style={{ borderRadius: 0 }}
          >
            {t.hero.cta_secondary}
          </button>
        </motion.div>

        {/* Scroll indicator */}
        <motion.button
          data-testid="hero-scroll-btn"
          onClick={() => scrollTo("about")}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.2 }}
          className="absolute bottom-10 left-6 sm:left-8 flex items-center gap-3 text-white/60 hover:text-white transition-colors text-xs uppercase tracking-[0.3em]"
        >
          <span className="block w-px h-12 bg-white/40 animate-pulse" />
          <ArrowDown size={14} />
          <span>{t.hero.scroll}</span>
        </motion.button>
      </div>
    </section>
  );
};
